import useLocale from '@hooks/useLocale';
import { getContentItems } from '@lib/prepr/content-item';
import { Locale } from '@type-declarations/prepr';
import getInternalRoute from '@utils/getInternalRoute';
import clsx from 'clsx';
import { forwardRef, useEffect, useMemo, useState } from 'react';

import styles from './RichText.module.scss';

interface Props {
  text: string;
  className?: string;
  store?: boolean;
}

/* Regex for removing anchor tags */
const anchorRegex = /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1[^>]*>(.*?)<\/a>/gi;

// Regex pattern to find content item UUIDs
// puuid#3046a3ef-05d7-4de9-9e4f-47f3357a953b
const uuidRegex =
  /puuid#([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})/g;

// Regex pattern to find asset UUIDs
// auuid#3046a3ef-05d7-4de9-9e4f-47f3357a953b
const assetRegex =
  /auuid#([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})/g;

const getInternalLinks = async ({
  ids,
  locale,
  text,
}: {
  ids: RegExpMatchArray;
  locale: Locale;
  text: string;
}) => {
  /* Get content items */
  const pages = await getContentItems({
    idAny: ids.map(id => id.substring(6)),
    allTypes: true,
    locale,
    limit: 100,
  });

  let newHTML = text;
  pages.items?.forEach(page => {
    newHTML = newHTML.replaceAll(
      `puuid#${page.id}`,
      `/${locale}/${getInternalRoute({
        internalLink: page,
        locale,
      })}`,
    );
  });

  return newHTML;
};

const getAssetLinks = async ({
  ids,
  text,
}: {
  ids: RegExpMatchArray;
  text: string;
}) => {
  // remove duplicates
  const fetchUrls = Array.from(
    new Set(ids.map(id => `/api/assets?uuid=${id.substring(6)}`)),
  ).filter(url => url !== undefined);

  const assets = await Promise.all(
    fetchUrls.map(url => fetch(url).then(res => res.json())),
  );

  let newHTML = text;

  assets.forEach(asset => {
    const url = asset.cdn_files?.items?.[0]?.url;
    if (url) {
      newHTML = newHTML.replaceAll(`auuid#${asset.id}`, url);
    }
  });

  return newHTML;
};

const RichText = forwardRef<HTMLDivElement, Props>(
  ({ text, store, className }, ref) => {
    const preview = useMemo(
      () =>
        text
          .replaceAll('\r', '<br>')
          .replaceAll('<a', '<span')
          .replaceAll('</a>', '</span>'),
      [text],
    );
    const [HTML, setHTML] = useState<string>('');

    const locale = useLocale();

    useEffect(() => {
      const checkForLinks = async () => {
        const uuids = text.match(uuidRegex);
        const auuids = text.match(assetRegex);

        let newText = text;

        if (uuids?.length) {
          newText = await getInternalLinks({
            ids: uuids,
            locale,
            text: newText,
          });
        }

        if (auuids?.length) {
          newText = await getAssetLinks({ ids: auuids, text: newText });
        }

        setHTML(newText.replaceAll('\r', '<br>'));
      };

      checkForLinks();
    }, [locale, text]);

    if (store) {
      return (
        <div
          ref={ref}
          className={clsx(styles.content, className)}
          dangerouslySetInnerHTML={{
            __html: HTML.replace(
              anchorRegex,
              (_0, _1, _2, p3) => p3, // return the label text
            ),
          }}
        />
      );
    }

    return (
      <div
        ref={ref}
        className={clsx(styles.content, className)}
        dangerouslySetInnerHTML={{
          __html: HTML || preview,
        }}
      />
    );
  },
);

RichText.displayName = 'RichText';

export default RichText;
